import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { Sprout, CalendarRange, ArrowLeft, MapPin, Beaker, Trash2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { PageHeader } from "@/components/PageHeader";
import { TimelineRenderer } from "@/components/timeline/TimelineRenderer";
import { DateInlineEditor } from "@/components/inline-editors/DateInlineEditor";
import { StatusInlineEditor } from "@/components/inline-editors/StatusInlineEditor";
import { authFetch } from "@/lib/auth";
import { toast } from "sonner";

interface Planting {
  id: string;
  ranch_id: string;
  crop_variety_id: string;
  growing_template_id: string;
  pasture_id: string;
  field_subdivision_id: string | null;
  succession_group_id: string | null;
  sow_date: string;
  transplant_date: string | null;
  expected_harvest_date: string | null;
  actual_harvest_date: string | null;
  status: "planned" | "sown" | "growing" | "harvesting" | "complete" | "failed";
  quantity_planted: number | null;
  quantity_unit: string | null;
  notes: string | null;
}

interface Task {
  id: string;
  title: string;
  status: string;
  start_date: string | null;
  rrule: string | null;
}

interface Harvest {
  id: string;
  harvest_date: string;
  quantity: number;
  unit: string;
  quality_notes: string | null;
}

interface PlantingDetailResponse {
  planting: Planting;
  tasks: Task[];
  harvests: Harvest[];
}

const STATUS_OPTIONS = [
  { value: "planned",    label: "Planned",    className: "bg-token-muted/15 text-token-muted border-token-muted/30 dark:bg-token-muted-dark/20 dark:text-token-muted-dark dark:border-token-muted-dark/30" },
  { value: "sown",       label: "Sown",       className: "bg-burnt-orange/15 text-burnt-orange border-burnt-orange/30 dark:bg-burnt-orange-dark/20 dark:text-burnt-orange-dark dark:border-burnt-orange-dark/30" },
  { value: "growing",    label: "Growing",    className: "bg-olive/15 text-olive border-olive/30 dark:bg-olive-dark/20 dark:text-olive-dark dark:border-olive-dark/30" },
  { value: "harvesting", label: "Harvesting", className: "bg-burnt-orange/15 text-burnt-orange border-burnt-orange/30 dark:bg-burnt-orange-dark/20 dark:text-burnt-orange-dark dark:border-burnt-orange-dark/30" },
  { value: "complete",   label: "Complete",   className: "bg-moss/15 text-moss border-moss/30 dark:bg-moss-dark/20 dark:text-moss-dark dark:border-moss-dark/30" },
  { value: "failed",     label: "Failed",     className: "bg-clay/15 text-clay border-clay/30 dark:bg-clay-dark/20 dark:text-clay-dark dark:border-clay-dark/30" },
];

export function PlantingDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const [data, setData] = useState<PlantingDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [timelineKey, setTimelineKey] = useState(0);

  const loadData = useCallback(async () => {
    if (!id) return;
    try {
      const result = await authFetch<PlantingDetailResponse>(`/api/plantings/${id}`);
      setData(result);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Failed to load planting";
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { loadData(); }, [loadData]);

  // Inline status edit (no cascade)
  const handleStatusChange = async (newStatus: string) => {
    if (!id || !data) return;
    await authFetch(`/api/plantings/${id}`, {
      method: "PATCH",
      body: JSON.stringify({ status: newStatus }),
    });
    setData({ ...data, planting: { ...data.planting, status: newStatus as Planting["status"] } });
    setTimelineKey((k) => k + 1);
  };

  if (loading) {
    return (
      <>
        <PageHeader title="Planting" breadcrumbs={[{ label: "Plantings", href: "/plantings" }, { label: "..." }]} />
        <Card className="p-6 space-y-3">
          <Skeleton className="h-6 w-1/3" />
          <Skeleton className="h-4 w-1/2" />
          <Skeleton className="h-32 w-full" />
        </Card>
      </>
    );
  }
  if (error || !data) {
    return (
      <>
        <PageHeader title="Planting" />
        <Card className="p-6">
          <p className="text-clay dark:text-clay-dark">{error || "Planting not found"}</p>
          <Button asChild variant="outline" className="mt-3">
            <Link to="/plantings"><ArrowLeft className="h-4 w-4 mr-1.5" /> Back to plantings</Link>
          </Button>
        </Card>
      </>
    );
  }

  const p = data.planting;

  return (
    <>
      <PageHeader
        title="Planting"
        breadcrumbs={[
          { label: "Dashboard", href: "/" },
          { label: "Plantings", href: "/plantings" },
          { label: p.id.slice(0, 8) },
        ]}
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        {/* Metadata card */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2">
                <Sprout className="h-5 w-5 text-ranch-green" />
                Planting Details
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Status</p>
                  <div className="mt-1">
                    <StatusInlineEditor
                      value={p.status}
                      options={STATUS_OPTIONS}
                      onChange={handleStatusChange}
                    />
                  </div>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Sow Date</p>
                  <div className="mt-1">
                    {/*
                      Cascade-aware date editor. Changing sow_date triggers
                      the cascade preview, which projects the move across
                      every linked task instance and harvest projection.
                    */}
                    <DateInlineEditor
                      value={p.sow_date}
                      onChange={async () => { /* unused — cascade flow handles it */ }}
                      cascade={{
                        payloadBuilder: (newDate) => ({
                          cascade_type: "planting_reschedule",
                          trigger_entity_type: "planting",
                          trigger_entity_id: p.id,
                          change: { sow_date: newDate },
                        }),
                        onApplied: (result) => {
                          toast.success(
                            `${result.affected_count} task${
                              result.affected_count === 1 ? "" : "s"
                            } rescheduled`,
                            {
                              action: {
                                label: "Undo",
                                onClick: async () => {
                                  try {
                                    await authFetch("/api/cascade/undo", {
                                      method: "POST",
                                      body: JSON.stringify({ audit_log_id: result.audit_log_id }),
                                    });
                                    toast.success("Reverted");
                                    loadData();
                                    setTimelineKey((k) => k + 1);
                                  } catch (err: unknown) {
                                    const msg = err instanceof Error ? err.message : "Undo failed";
                                    toast.error(msg);
                                  }
                                },
                              },
                              duration: 30_000,
                            },
                          );
                          loadData();
                          setTimelineKey((k) => k + 1);
                        },
                      }}
                    />
                  </div>
                </div>
                {p.transplant_date && (
                  <div>
                    <p className="text-sm text-muted-foreground">Transplant Date</p>
                    <p className="font-medium mt-1">{p.transplant_date.split("T")[0]}</p>
                  </div>
                )}
                {p.expected_harvest_date && (
                  <div>
                    <p className="text-sm text-muted-foreground">Expected Harvest</p>
                    <p className="font-medium mt-1">{p.expected_harvest_date.split("T")[0]}</p>
                  </div>
                )}
                {p.actual_harvest_date && (
                  <div>
                    <p className="text-sm text-muted-foreground">Actual Harvest</p>
                    <p className="font-medium mt-1">{p.actual_harvest_date.split("T")[0]}</p>
                  </div>
                )}
                {p.quantity_planted !== null && (
                  <div>
                    <p className="text-sm text-muted-foreground">Quantity Planted</p>
                    <p className="font-medium mt-1">
                      {p.quantity_planted} {p.quantity_unit ?? ""}
                    </p>
                  </div>
                )}
                {p.notes && (
                  <div className="col-span-2">
                    <p className="text-sm text-muted-foreground">Notes</p>
                    <p className="text-sm mt-1">{p.notes}</p>
                  </div>
                )}
                {p.succession_group_id && (
                  <div className="col-span-2">
                    <Badge variant="outline" className="bg-sky-muted/10 text-sky-muted border-sky-muted/30 dark:bg-sky-muted-dark/15 dark:text-sky-muted-dark dark:border-sky-muted-dark/30">
                      Part of succession group
                    </Badge>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tasks + harvests sidebar */}
        <div className="space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Beaker className="h-4 w-4 text-ranch-brown" /> Tasks ({data.tasks.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {data.tasks.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">No tasks.</p>
              ) : (
                data.tasks.slice(0, 8).map((t) => (
                  <div key={t.id} className="text-sm border-l-2 border-token-border dark:border-token-border-dark pl-2 py-1">
                    <p className="font-medium truncate">{t.title}</p>
                    <p className="text-xs text-muted-foreground">
                      {t.start_date?.split("T")[0]}
                      {t.rrule && " · recurring"}
                    </p>
                  </div>
                ))
              )}
              {data.tasks.length > 8 && (
                <p className="text-xs text-muted-foreground italic">
                  +{data.tasks.length - 8} more …
                </p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <CalendarRange className="h-4 w-4 text-burnt-orange dark:text-burnt-orange-dark" /> Harvests ({data.harvests.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {data.harvests.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">No harvests yet.</p>
              ) : (
                data.harvests.map((h) => (
                  <div key={h.id} className="text-sm border-l-2 border-burnt-orange/60 dark:border-burnt-orange-dark/60 pl-2 py-1">
                    <p className="font-medium">
                      {h.quantity} {h.unit}
                    </p>
                    <p className="text-xs text-muted-foreground">{h.harvest_date.split("T")[0]}</p>
                    {h.quality_notes && (
                      <p className="text-xs italic text-muted-foreground">{h.quality_notes}</p>
                    )}
                  </div>
                ))
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Timeline */}
      <div>
        <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
          <CalendarRange className="h-4 w-4 text-ranch-brown" />
          Timeline
        </h2>
        <TimelineRenderer
          entityType="planting"
          entityId={id!}
          refreshKey={timelineKey}
          allowAnnotations
          onAnnotationCreated={() => setTimelineKey((k) => k + 1)}
        />
      </div>
    </>
  );
}
